import { useState } from 'react'
import type { ShippingInfo } from '../types'
import { isValidEmail, isValidPhone } from '../lib/utils'

interface ShippingFormProps {
  onSubmit: (info: ShippingInfo) => void
  isLoading: boolean
  error?: string
}

function ShippingForm({ onSubmit, isLoading, error }: ShippingFormProps) {
  const [formData, setFormData] = useState<ShippingInfo>({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
  })

  const [errors, setErrors] = useState<Partial<Record<keyof ShippingInfo, string>>>({})

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ShippingInfo, string>> = {}

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Nama wajib diisi'
    }

    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = 'Nomor telepon wajib diisi'
    } else if (!isValidPhone(formData.customerPhone)) {
      newErrors.customerPhone = 'Nomor telepon tidak valid'
    }

    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = 'Email wajib diisi'
    } else if (!isValidEmail(formData.customerEmail)) {
      newErrors.customerEmail = 'Email tidak valid'
    }

    if (!formData.customerAddress.trim()) {
      newErrors.customerAddress = 'Alamat wajib diisi'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (validateForm()) {
      onSubmit(formData)
    }
  }

  const handleChange = (field: keyof ShippingInfo, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-6">
      <h2 className="text-xl font-bold">Informasi Pengiriman</h2>

      {error && (
        <div className="bg-error bg-opacity-10 border border-error text-error px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="customerName" className="block font-medium mb-2">
          Nama Lengkap <span className="text-error">*</span>
        </label>
        <input
          id="customerName"
          type="text"
          value={formData.customerName}
          onChange={(e) => handleChange('customerName', e.target.value)}
          className={`input-field ${errors.customerName ? 'border-error' : ''}`}
          placeholder="Masukkan nama lengkap"
          disabled={isLoading}
        />
        {errors.customerName && (
          <p className="text-error text-sm mt-1">{errors.customerName}</p>
        )}
      </div>

      <div>
        <label htmlFor="customerPhone" className="block font-medium mb-2">
          Nomor Telepon <span className="text-error">*</span>
        </label>
        <input
          id="customerPhone"
          type="tel"
          value={formData.customerPhone}
          onChange={(e) => handleChange('customerPhone', e.target.value)}
          className={`input-field ${errors.customerPhone ? 'border-error' : ''}`}
          placeholder="0812-3456-7890"
          disabled={isLoading}
        />
        {errors.customerPhone && (
          <p className="text-error text-sm mt-1">{errors.customerPhone}</p>
        )}
      </div>

      <div>
        <label htmlFor="customerEmail" className="block font-medium mb-2">
          Email <span className="text-error">*</span>
        </label>
        <input
          id="customerEmail"
          type="email"
          value={formData.customerEmail}
          onChange={(e) => handleChange('customerEmail', e.target.value)}
          className={`input-field ${errors.customerEmail ? 'border-error' : ''}`}
          placeholder="email@example.com"
          disabled={isLoading}
        />
        {errors.customerEmail && (
          <p className="text-error text-sm mt-1">{errors.customerEmail}</p>
        )}
      </div>

      <div>
        <label htmlFor="customerAddress" className="block font-medium mb-2">
          Alamat Lengkap <span className="text-error">*</span>
        </label>
        <textarea
          id="customerAddress"
          value={formData.customerAddress}
          onChange={(e) => handleChange('customerAddress', e.target.value)}
          className={`input-field ${errors.customerAddress ? 'border-error' : ''}`}
          placeholder="Masukkan alamat lengkap dengan kode pos"
          rows={4}
          disabled={isLoading}
        />
        {errors.customerAddress && (
          <p className="text-error text-sm mt-1">{errors.customerAddress}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full py-3"
      >
        {isLoading ? 'Memproses...' : 'Buat Pesanan'}
      </button>
    </form>
  )
}

export default ShippingForm
